import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS, toAuditorPortalPageUrl } from "../../config/api";
import { featureFlags } from "../../config/featureFlags";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import { GovernanceListingSection } from "../../components/governance/GovernanceListingSection";
import { ComplianceCenterMetricTile } from "../../components/compliance-center/ComplianceCenterMetricTile";
import { ComplianceCenterListRow } from "../../components/compliance-center/ComplianceCenterListRow";
import { Button } from "../../components/ui/Button";
import { Select } from "../../components/ui/Select";
import { TextField } from "../../components/ui/TextField";
import listStyles from "@/styles/pages/governance/GovernanceListing.module.scss";

type WorkflowRow = {
  id: string;
  name?: string;
  status?: string;
  standardCode?: string;
};

export default function ComplianceCenterCertificationWorkspace() {
  const { t } = useI18n();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState<WorkflowRow[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState({
    name: "",
    standardCode: "SOC2",
    baselineMode: "LATEST_COMPLETED_ASSESSMENT",
    historicalAuditDisclosureLevel: "FINAL_DECISION_ONLY",
    historicalEvidenceReuseMode: "SUGGEST_FOR_APPROVAL",
  });
  const [inviteWorkflowId, setInviteWorkflowId] = useState<string | null>(null);
  const [inviteForm, setInviteForm] = useState({
    auditorEmail: "",
    auditorName: "",
    auditorRole: "AUDITOR",
  });
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setWorkflows([]);
      setPackages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [workflowsRes, packagesRes] = await Promise.all([
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOWS, { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_PACKAGES, { headers }),
      ]);
      const workflowsJson = workflowsRes.ok ? await workflowsRes.json().catch(() => ({})) : {};
      const packagesJson = packagesRes.ok ? await packagesRes.json().catch(() => ({})) : {};
      setWorkflows(Array.isArray(workflowsJson?.data) ? workflowsJson.data : []);
      setPackages(Array.isArray(packagesJson?.data) ? packagesJson.data : []);
    } catch {
      setWorkflows([]);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const underReview = useMemo(
    () => workflows.filter((item) => String(item?.status || "").toLowerCase().includes("review")).length,
    [workflows],
  );

  const handleDeleteWorkflow = async (workflow: WorkflowRow) => {
    if (!token || !workflow.id) return;
    const label = workflow.name || workflow.id;
    if (!window.confirm(t("complianceCenterUi.deleteWorkflowConfirm", `Delete certification workflow "${label}"?`))) {
      return;
    }
    setDeletingId(workflow.id);
    try {
      const res = await fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW(workflow.id), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Delete failed");
      }
      await load();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Delete failed";
      window.alert(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateWorkflow = async () => {
    if (!token || !newWorkflow.name.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOWS, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newWorkflow.name.trim(),
          standardCode: newWorkflow.standardCode,
          status: "draft",
          baselineMode: newWorkflow.baselineMode,
          historicalAuditDisclosureLevel: newWorkflow.historicalAuditDisclosureLevel,
          historicalEvidenceReuseMode: newWorkflow.historicalEvidenceReuseMode,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || "Create failed");
      setNewWorkflow({
        name: "",
        standardCode: "SOC2",
        baselineMode: "LATEST_COMPLETED_ASSESSMENT",
        historicalAuditDisclosureLevel: "FINAL_DECISION_ONLY",
        historicalEvidenceReuseMode: "SUGGEST_FOR_APPROVAL",
      });
      await load();
    } catch (err: unknown) {
      window.alert(err instanceof Error ? err.message : "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const handleInviteAuditor = async () => {
    if (!token || !inviteWorkflowId || !inviteForm.auditorEmail.trim() || inviting) return;
    setInviteUrl(null);
    setInviteError(null);
    setInviting(true);
    try {
      const res = await fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_AUDITOR_TOKEN(inviteWorkflowId), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          auditorEmail: inviteForm.auditorEmail.trim(),
          auditorName: inviteForm.auditorName.trim() || null,
          auditorRole: inviteForm.auditorRole,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || "Invite failed");
      const url = toAuditorPortalPageUrl(body?.data?.portalUrl);
      if (!url) throw new Error(t("certification.workflowDetail.errors.portalUrlMissing", "Portal URL was not returned"));
      setInviteUrl(url);
      await navigator.clipboard.writeText(url).catch(() => null);
    } catch (err: unknown) {
      setInviteError(err instanceof Error ? err.message : t("certification.workflowDetail.errors.inviteFailed", "Invite failed"));
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t("complianceCenterUi.nav.certification", "Certification")}
        description={t(
          "complianceCenterUi.certificationIntro",
          "Workflows, packages, auditor review status, and decision lifecycle.",
        )}
      >
        {loading ? (
          <p className={listStyles.panelSubtitle}>{t("common.loading", "Loading…")}</p>
        ) : (
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
            <ComplianceCenterMetricTile label={t("complianceCenterUi.workflows", "Workflows")} value={workflows.length} tone="blue" />
            <ComplianceCenterMetricTile label={t("complianceCenterUi.packages", "Packages")} value={packages.length} tone="purple" />
            <ComplianceCenterMetricTile
              label={t("complianceCenterUi.underReview", "Under auditor review")}
              value={underReview}
              tone="orange"
            />
            <ComplianceCenterMetricTile
              label={t("complianceCenterUi.auditorPortal", "Auditor portal")}
              value={featureFlags.AUDITOR_PORTAL_V1 ? t("common.enabled", "Enabled") : t("common.disabled", "Disabled")}
              tone="green"
            />
          </div>
        )}
      </GovernanceListingSection>

      {!loading && featureFlags.CERTIFICATION_WORKFLOW_V1 ? (
        <GovernanceListingSection title={t("complianceCenterUi.createWorkflow", "Create certification workflow")}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <TextField
              label={t("complianceCenterUi.workflowName", "Workflow name")}
              value={newWorkflow.name}
              onChange={(value) => setNewWorkflow((p) => ({ ...p, name: value }))}
              placeholder={t("complianceCenterUi.workflowName", "Workflow name")}
            />
            <Select
              label={t("complianceCenterUi.standard", "Standard")}
              value={newWorkflow.standardCode}
              onChange={(value) => setNewWorkflow((p) => ({ ...p, standardCode: String(value || "SOC2") }))}
              options={["SOC2", "NIS2", "ISO27001", "ISO42001", "GDPR"].map((code) => ({ value: code, label: code }))}
            />
            <Select
              label={t("certification.workflowDetail.baselineMode", "Baseline mode")}
              value={newWorkflow.baselineMode}
              onChange={(value) => setNewWorkflow((p) => ({ ...p, baselineMode: String(value || "LATEST_COMPLETED_ASSESSMENT") }))}
              options={[
                { value: "LATEST_COMPLETED_ASSESSMENT", label: t("certification.workflowDetail.baselineModes.latest", "Latest completed assessment") },
                { value: "SELECTED_ASSESSMENT_SESSION", label: t("certification.workflowDetail.baselineModes.selected", "Selected assessment session") },
                { value: "PREVIOUS_CERTIFICATION_WORKFLOW", label: t("certification.workflowDetail.baselineModes.previous", "Previous certification workflow") },
                { value: "CURRENT_STATE_SNAPSHOT", label: t("certification.workflowDetail.baselineModes.current", "Current state snapshot") },
              ]}
            />
            <Select
              label={t("complianceCenterUi.historicalDisclosure", "Historical disclosure")}
              value={newWorkflow.historicalAuditDisclosureLevel}
              onChange={(value) => setNewWorkflow((p) => ({ ...p, historicalAuditDisclosureLevel: String(value || "FINAL_DECISION_ONLY") }))}
              options={[
                { value: "NONE", label: t("complianceCenterUi.historyNone", "History: none") },
                { value: "FINAL_DECISION_ONLY", label: t("complianceCenterUi.historyDecision", "History: final decision only") },
                { value: "FINDINGS_AND_REMEDIATION", label: t("complianceCenterUi.historyFindings", "History: findings and remediation") },
                { value: "FULL_PREVIOUS_CERTIFICATION_PACKAGE", label: t("complianceCenterUi.historyFull", "History: full previous package") },
              ]}
            />
            <Select
              label={t("complianceCenterUi.evidenceReuse", "Evidence reuse")}
              value={newWorkflow.historicalEvidenceReuseMode}
              onChange={(value) => setNewWorkflow((p) => ({ ...p, historicalEvidenceReuseMode: String(value || "SUGGEST_FOR_APPROVAL") }))}
              options={[
                { value: "DO_NOT_REUSE", label: t("complianceCenterUi.reuseNone", "Reuse: do not reuse") },
                { value: "SUGGEST_FOR_APPROVAL", label: t("complianceCenterUi.reuseSuggest", "Reuse: suggest for approval") },
                { value: "INCLUDE_PREVIOUSLY_APPROVED", label: t("complianceCenterUi.reuseInclude", "Reuse: include previously approved") },
              ]}
            />
            <Button type="button" size="small" color="primary" disabled={creating} onClick={() => void handleCreateWorkflow()}>
              {creating ? t("common.creating", "Creating…") : t("common.create", "Create")}
            </Button>
          </div>
        </GovernanceListingSection>
      ) : null}

      {!loading ? (
        <GovernanceListingSection title={t("complianceCenterUi.certificationWorkflows", "Certification workflows")}>
          <div className={listStyles.listStack}>
            {workflows.slice(0, 20).map((workflow) => (
              <ComplianceCenterListRow
                key={workflow.id}
                title={workflow.name || workflow.id}
                meta={`${t("common.status", "Status")}: ${t(`certification.status.lifecycle.${String(workflow.lifecycle?.stage || "PREPARATION")}`, String(workflow.lifecycle?.stage || workflow.status || "draft").replaceAll("_", " "))} · ${workflow.standardCode || "—"}`}
                actions={
                  <>
                    <Button
                      type="button"
                      size="small"
                      color="primary"
                      onClick={() => navigate(`/compliance-center/certification/${workflow.id}`)}
                    >
                      {t("certification.workspace.openAudit", "Open audit")}
                    </Button>
                    {featureFlags.AUDITOR_PORTAL_V1 ? (
                      <Button
                        type="button"
                        size="small"
                        color="secondary"
                        onClick={() => {
                          setInviteWorkflowId(workflow.id);
                          setInviteUrl(null);
                          setInviteError(null);
                        }}
                      >
                        {t("complianceCenterUi.inviteAuditor", "Invite auditor")}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="small"
                      color="danger"
                      disabled={deletingId === workflow.id}
                      onClick={() => void handleDeleteWorkflow(workflow)}
                    >
                      {deletingId === workflow.id ? t("common.deleting", "Deleting…") : t("common.delete", "Delete")}
                    </Button>
                  </>
                }
              />
            ))}
            {!workflows.length ? (
              <p className={listStyles.panelSubtitle}>{t("complianceCenterUi.noWorkflows", "No workflows found.")}</p>
            ) : null}
          </div>
        </GovernanceListingSection>
      ) : null}

      {inviteWorkflowId ? (
        <GovernanceListingSection title={t("complianceCenterUi.inviteAuditor", "Invite auditor")}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 480 }}>
            <TextField
              label={t("complianceCenterUi.auditorEmail", "Auditor email")}
              value={inviteForm.auditorEmail}
              onChange={(value) => setInviteForm((p) => ({ ...p, auditorEmail: value }))}
              placeholder={t("complianceCenterUi.auditorEmail", "Auditor email")}
            />
            <TextField
              label={t("complianceCenterUi.auditorName", "Auditor name")}
              value={inviteForm.auditorName}
              onChange={(value) => setInviteForm((p) => ({ ...p, auditorName: value }))}
              placeholder={t("complianceCenterUi.auditorName", "Auditor name")}
            />
            <Select
              label={t("certification.workflowDetail.auditorRole", "Auditor role")}
              value={inviteForm.auditorRole}
              onChange={(value) => setInviteForm((p) => ({ ...p, auditorRole: String(value || "AUDITOR") }))}
              options={["LEAD_AUDITOR", "AUDITOR", "REVIEWER", "OBSERVER"].map((role) => ({
                value: role,
                label: t(`certification.roles.${role}`, role.replaceAll("_", " ")),
              }))}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Button type="button" size="small" color="primary" disabled={inviting || !inviteForm.auditorEmail.trim()} onClick={() => void handleInviteAuditor()}>
                {inviting ? t("common.saving", "Saving...") : t("complianceCenterUi.sendInvite", "Send invite")}
              </Button>
              <Button type="button" size="small" color="outline" onClick={() => setInviteWorkflowId(null)}>
                {t("common.cancel", "Cancel")}
              </Button>
            </div>
            {inviteError ? <p className={listStyles.formError}>{inviteError}</p> : null}
            {inviteUrl ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <p className={listStyles.panelSubtitle}>
                  {t("certification.workflowDetail.success.invitationSent", "Invitation sent")}
                </p>
                <TextField
                  readOnly
                  label={t("certification.workflowDetail.portalUrlLabel", "Auditor portal link")}
                  value={inviteUrl}
                  onChange={() => undefined}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    type="button"
                    size="small"
                    color="secondary"
                    onClick={async () => {
                      await navigator.clipboard.writeText(inviteUrl).catch(() => null);
                    }}
                  >
                    {t("certification.workflowDetail.copyPortalUrl", "Copy portal URL")}
                  </Button>
                  <a href={inviteUrl} target="_blank" rel="noreferrer">
                    <Button type="button" size="small" color="outline">
                      {t("certification.workflowDetail.openPortalUrl", "Open portal link")}
                    </Button>
                  </a>
                </div>
              </div>
            ) : null}
          </div>
        </GovernanceListingSection>
      ) : null}
    </div>
  );
}
